import { doc, setDoc, getDoc, getDocs, collection, query, where, orderBy, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { User } from './types';
import { generateTeamMemberId } from './idGeneratorService';

export class UserService {
  async createUser(userData: Omit<User, 'id' | 'createdAt' | 'updatedAt' | 'teamMemberId'>, uid?: string): Promise<User> {
    try {
      const userId = uid || doc(collection(db, 'users')).id;
      const userRef = doc(db, 'users', userId);
      
      // Generuj team member ID ak má companyId
      let teamMemberId: string | undefined;
      if (userData.companyId) {
        teamMemberId = await generateTeamMemberId(userData.companyId);
      }
      
      const user: User = {
        ...userData,
        id: userId,
        teamMemberId,
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
      };
      
      await setDoc(userRef, user);
      return user;
    } catch (error) {
      console.error('Chyba pri vytváraní používateľa:', error);
      throw new Error('Nepodarilo sa vytvoriť používateľa');
    }
  }

  async getUser(userId: string): Promise<User | null> {
    try {
      if (!userId) {
        return null;
      }

      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        return userData;
      }
      
      return null;
    } catch (error) {
      console.error('Chyba pri načítaní používateľa:', error);
      return null;
    }
  }

  async updateUser(userId: string, updates: Partial<User>): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Chyba pri aktualizácii používateľa:', error);
      throw new Error('Nepodarilo sa aktualizovať používateľa');
    }
  }

  async getUsersByCompany(companyId: string): Promise<User[]> {
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('companyId', '==', companyId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(usersQuery);
      return querySnapshot.docs.map(doc => doc.data() as User);
    } catch (error) {
      console.error('Chyba pri načítaní používateľov firmy:', error);
      throw new Error('Nepodarilo sa načítať používateľov firmy');
    }
  }

  async getAllUsers(): Promise<User[]> {
    try {
      const usersQuery = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(usersQuery);
      const users: User[] = [];
      querySnapshot.forEach((doc) => {
        users.push(doc.data() as User);
      });
      
      return users;
    } catch (error) {
      console.error('Chyba pri načítaní všetkých používateľov:', error);
      throw new Error('Nepodarilo sa načítať používateľov');
    }
  }
}

export const userService = new UserService();

